import { MiddleTruncatedText } from 'features/knowledgeDocumentViewer/components/MiddleTruncatedText'
import { useInstrumentTimeSeriesData } from 'features/knowledgeDocumentViewer/hooks/useInstrumentTimeSeriesData'
import { SensorSearchItem } from 'features/knowledgeDocumentViewer/types/searchableItems'
import {
  ColorType,
  createChart,
  IChartApi,
  LineData,
  LineSeries,
  Time,
} from 'lightweight-charts'
import { LineChart } from 'lucide-react'
import React, { useEffect, useMemo, useRef } from 'react'
import styled, { keyframes } from 'styled-components'

const CARD_WIDTH = 120
const SPARKLINE_HEIGHT = 50

const Card = styled.button<{ $isSelected?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: ${CARD_WIDTH}px;
  flex-shrink: 0;
  padding: 0.25rem;
  background: ${(props) =>
    props.$isSelected
      ? props.theme.forecast.activeButton
      : props.theme.forecast.defaultButton};
  border-radius: 0.25rem;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  border: 2px solid transparent;

  &:hover {
    background-color: ${(props) =>
      props.$isSelected
        ? props.theme.forecast.hoverGhostButton
        : props.theme.forecast.hoverButton};
  }
  &:active {
    background-color: ${(props) => props.theme.forecast.activeButton};
    color: ${(props) => props.theme.forecast.activeText};
  }
`

const SparklineArea = styled.div`
  width: 100%;
  height: ${SPARKLINE_HEIGHT}px;
  margin-top: 0.375rem;
  border-radius: 0.125rem;
  overflow: hidden;
`

const PlaceholderArea = styled.div`
  width: 100%;
  height: ${SPARKLINE_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.125rem;
  color: ${(props) => props.theme.forecast.secondaryText};
  opacity: 0.3;
`

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`

const SparklineSkeleton = styled.div`
  width: 100%;
  height: ${SPARKLINE_HEIGHT}px;
  border-radius: 0.125rem;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.forecast.cardGroupBackground} 25%,
    ${({ theme }) => theme.forecast.hoverButton} 50%,
    ${({ theme }) => theme.forecast.cardGroupBackground} 75%
  );
  background-size: 400px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0.25rem 0 0.25rem;
  gap: 0.0625rem;
  margin-top: auto;
`

const ValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${(props) => props.theme.forecast.defaultText};
`

const UnitText = styled.span`
  font-size: 0.5625rem;
  font-weight: 400;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const TimestampText = styled.div`
  font-size: 0.5625rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const Title = styled.div`
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${(props) => props.theme.forecast.defaultText};
`

function formatCompactValue(val: number): string {
  if (Math.abs(val) >= 1000000) return `${(val / 1000000).toFixed(1)}M`
  if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)}K`
  if (Math.abs(val) < 0.01 && val !== 0) return val.toExponential(1)
  return val.toFixed(1)
}

function formatCompactTime(date: Date): string {
  const now = new Date()
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

const SPARKLINE_COLOR = '#22d3ee'

function MiniSparkline({ data }: { data: { time: number; value: number }[] }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return

    const container = containerRef.current
    const width = container.clientWidth || CARD_WIDTH - 4

    const chart = createChart(container, {
      width,
      height: SPARKLINE_HEIGHT,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: 'transparent',
        attributionLogo: false,
      },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      crosshair: {
        vertLine: { visible: false },
        horzLine: { visible: false },
      },
      timeScale: { visible: false, borderVisible: false },
      rightPriceScale: { visible: false, borderVisible: false },
      leftPriceScale: { visible: false, borderVisible: false },
      handleScroll: false,
      handleScale: false,
    })

    chartRef.current = chart

    const series = chart.addSeries(LineSeries, {
      color: SPARKLINE_COLOR,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    })

    const chartData: LineData[] = data
      .map((p) => ({ time: (p.time / 1000) as Time, value: p.value }))
      .sort((a, b) => (a.time as number) - (b.time as number))

    series.setData(chartData)
    chart.timeScale().fitContent()

    return () => {
      chart.remove()
      chartRef.current = null
    }
  }, [data])

  return <SparklineArea ref={containerRef} />
}

interface RecentSensorCardProps {
  sensor: SensorSearchItem
  isSelected?: boolean
  onClick: (sensor: SensorSearchItem, event?: React.MouseEvent) => void
}

export const RecentSensorCard: React.FC<RecentSensorCardProps> = ({
  sensor,
  isSelected = false,
  onClick,
}) => {
  const sensorIds = useMemo(() => [sensor.sensorId], [sensor.sensorId])
  const { sparklineData, isLoading } = useInstrumentTimeSeriesData(sensorIds)

  const sparkline = sparklineData[0] ?? null

  const handleClick = (event: React.MouseEvent) => {
    onClick(sensor, event)
  }

  return (
    <Card onClick={handleClick} $isSelected={isSelected}>
      {isLoading ? (
        <SparklineSkeleton />
      ) : sparkline && sparkline.data.length > 0 ? (
        <MiniSparkline data={sparkline.data} />
      ) : (
        <PlaceholderArea>
          <LineChart size={20} />
        </PlaceholderArea>
      )}
      <CardContent>
        {sparkline ? (
          <>
            <ValueRow>
              {formatCompactValue(sparkline.currentValue)}
              {sparkline.unit && <UnitText>{sparkline.unit}</UnitText>}
            </ValueRow>
            <TimestampText>
              {formatCompactTime(sparkline.lastMeasuredAt)}
            </TimestampText>
          </>
        ) : (
          sensor.unit && <UnitText>{sensor.unit}</UnitText>
        )}
        <Title>
          <MiddleTruncatedText
            text={sensor.displayName}
            maxWidth='100%'
            fillWidth
            tooltipFontSize='0.6875rem'
          />
        </Title>
      </CardContent>
    </Card>
  )
}
